import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
   Box,
   Button,
   Checkbox,
   Chip,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogContentText,
   DialogTitle,
   FormControlLabel,
   FormGroup,
   IconButton,
   MenuItem,
   Paper,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   TextField,
   Tooltip,
   Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { withFirebase } from '../Firebase';
import useClipboard from '../../hooks/useClipboard';
import ApiPlayground from './apiPlayground';

const SCOPE_OPTIONS = [
   { value: 'schedule', label: 'Schedule (daily statuses & summary)' },
   { value: 'bookings', label: 'Bookings (space bookings)' },
   { value: 'users', label: 'Users (people directory)' },
];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function ApiKeys(props) {
   const { firebase, company } = props;
   const { enqueueSnackbar } = useSnackbar();
   const copy = useClipboard();

   const cid = company?.cid;
   const locations = company?.locations || {};

   // SettingsPage already resolved the raw flag and only mounts this tab when
   // enabled, so prefer the passed value. Fall back to reading the company doc
   // directly (the CompanyProvider strips `features.api`). null = still checking.
   const [apiEnabled, setApiEnabled] = useState(
      props.apiEnabled === undefined ? null : props.apiEnabled,
   );

   const [keys, setKeys] = useState([]);
   const [loading, setLoading] = useState(true);

   // Create dialog state
   const [createOpen, setCreateOpen] = useState(false);
   const [name, setName] = useState('');
   const [scopes, setScopes] = useState([]);
   const [allowedLocations, setAllowedLocations] = useState([]);
   const [submitting, setSubmitting] = useState(false);

   // One-time reveal of a newly created key
   const [newKey, setNewKey] = useState(null);
   const keyFieldRef = useRef(null);

   useEffect(() => {
      // If the parent already told us, don't re-read.
      if (props.apiEnabled !== undefined) return undefined;
      if (!cid) return undefined;
      let active = true;
      firebase
         .company(cid)
         .get()
         .then((snap) => {
            if (active) setApiEnabled(!!snap.data()?.features?.api);
         })
         .catch((err) => {
            console.log('Error checking API feature flag', err);
            if (active) setApiEnabled(false);
         });
      return () => {
         active = false;
      };
   }, [firebase, cid, props.apiEnabled]);

   const loadKeys = useCallback(async () => {
      if (!apiEnabled) {
         setLoading(false);
         return;
      }
      setLoading(true);
      try {
         const res = await firebase.listApiKeys();
         setKeys(res?.data?.keys || []);
      } catch (err) {
         console.log('Error loading API keys', err);
         enqueueSnackbar('Could not load API keys', { variant: 'error' });
      }
      setLoading(false);
   }, [firebase, apiEnabled, enqueueSnackbar]);

   useEffect(() => {
      loadKeys();
   }, [loadKeys]);

   const toggleScope = (value) =>
      setScopes((prev) =>
         prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value],
      );

   const resetForm = () => {
      setName('');
      setScopes([]);
      setAllowedLocations([]);
   };

   const handleCreate = async () => {
      if (!name.trim()) {
         enqueueSnackbar('Please give the key a name', { variant: 'warning' });
         return;
      }
      if (scopes.length === 0) {
         enqueueSnackbar('Select at least one scope', { variant: 'warning' });
         return;
      }
      setSubmitting(true);
      try {
         const res = await firebase.createApiKey({
            name: name.trim(),
            scopes,
            allowedLocations,
         });
         setCreateOpen(false);
         resetForm();
         setNewKey(res?.data || null);
         await loadKeys();
      } catch (err) {
         console.log('Error creating API key', err);
         enqueueSnackbar(err?.message || 'Could not create API key', {
            variant: 'error',
         });
      }
      setSubmitting(false);
   };

   const handleRevoke = async (keyId) => {
      if (
         !window.confirm(
            'Delete this API key? This cannot be undone and any application using it will stop working immediately.',
         )
      )
         return;
      try {
         await firebase.revokeApiKey(keyId);
         enqueueSnackbar('API key deleted', { variant: 'success' });
         await loadKeys();
      } catch (err) {
         console.log('Error deleting API key', err);
         enqueueSnackbar(err?.message || 'Could not delete API key', {
            variant: 'error',
         });
      }
   };

   const copyKey = () => {
      if (newKey?.apiKey) {
         copy(newKey.apiKey, 'API key copied to clipboard', keyFieldRef.current);
      }
   };

   if (apiEnabled === null) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (!apiEnabled) {
      return (
         <Box sx={{ mt: 2, p: 3 }} component={Paper}>
            <Typography variant="h5" gutterBottom>
               API access
            </Typography>
            <Typography variant="body1">
               API access is not enabled for your company. Contact support to enable
               it.
            </Typography>
         </Box>
      );
   }

   return (
      <Box sx={{ mt: 2 }}>
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               mb: 2,
            }}
         >
            <Box>
               <Typography variant="h5">API keys</Typography>
               <Typography variant="body2" color="textSecondary">
                  Create keys to access the intheOffice REST API. Keys are shown once
                  on creation — store them securely.
               </Typography>
            </Box>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
               Create API key
            </Button>
         </Box>

         <Paper>
            {loading ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
               </Box>
            ) : keys.length === 0 ? (
               <Box sx={{ p: 3 }}>
                  <Typography variant="body1">No API keys yet.</Typography>
               </Box>
            ) : (
               <Table>
                  <TableHead>
                     <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Scopes</TableCell>
                        <TableCell>Locations</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Last used</TableCell>
                        <TableCell align="right">Actions</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {keys.map((k) => (
                        <TableRow key={k.keyId}>
                           <TableCell>{k.name}</TableCell>
                           <TableCell>
                              {(k.scopes || []).map((s) => (
                                 <Chip
                                    key={s}
                                    label={s}
                                    size="small"
                                    sx={{ mr: 0.5 }}
                                 />
                              ))}
                           </TableCell>
                           <TableCell>
                              {!k.allowedLocations || k.allowedLocations.length === 0
                                 ? 'All'
                                 : k.allowedLocations
                                      .map((lid) => locations[lid] || lid)
                                      .join(', ')}
                           </TableCell>
                           <TableCell>{formatDate(k.createdAt)}</TableCell>
                           <TableCell>{formatDate(k.lastUsedAt)}</TableCell>
                           <TableCell align="right">
                              <Button
                                 size="small"
                                 color="error"
                                 onClick={() => handleRevoke(k.keyId)}
                              >
                                 Delete
                              </Button>
                           </TableCell>
                        </TableRow>
                     ))}
                  </TableBody>
               </Table>
            )}
         </Paper>

         {/* Usage docs + interactive playground */}
         <ApiPlayground company={company} lastCreatedKey={newKey?.apiKey || ''} />

         {/* Create dialog */}
         <Dialog
            open={createOpen}
            onClose={() => !submitting && setCreateOpen(false)}
            fullWidth
            maxWidth="sm"
         >
            <DialogTitle>Create API key</DialogTitle>
            <DialogContent>
               <TextField
                  label="Name"
                  fullWidth
                  margin="normal"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Reception display"
               />

               <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Scopes
               </Typography>
               <FormGroup>
                  {SCOPE_OPTIONS.map((opt) => (
                     <FormControlLabel
                        key={opt.value}
                        control={
                           <Checkbox
                              checked={scopes.includes(opt.value)}
                              onChange={() => toggleScope(opt.value)}
                           />
                        }
                        label={opt.label}
                     />
                  ))}
               </FormGroup>

               <TextField
                  select
                  SelectProps={{ multiple: true }}
                  label="Allowed locations (leave empty for all)"
                  fullWidth
                  margin="normal"
                  value={allowedLocations}
                  onChange={(e) => setAllowedLocations(e.target.value)}
                  helperText="Restrict this key to specific locations, or leave empty to allow all."
               >
                  {Object.entries(locations).map(([lid, locName]) => (
                     <MenuItem key={lid} value={lid}>
                        {locName}
                     </MenuItem>
                  ))}
               </TextField>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setCreateOpen(false)} disabled={submitting}>
                  Cancel
               </Button>
               <Button
                  variant="contained"
                  onClick={handleCreate}
                  disabled={submitting}
               >
                  {submitting ? 'Creating…' : 'Create'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* One-time reveal dialog */}
         <Dialog open={!!newKey} onClose={() => setNewKey(null)} fullWidth maxWidth="sm">
            <DialogTitle>API key created</DialogTitle>
            <DialogContent>
               <DialogContentText sx={{ mb: 2 }}>
                  Copy this key now — it will not be shown again. Store it securely.
               </DialogContentText>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                     fullWidth
                     value={newKey?.apiKey || ''}
                     inputRef={keyFieldRef}
                     onFocus={(e) => e.target.select()}
                     InputProps={{
                        readOnly: true,
                        sx: { fontFamily: 'monospace', fontSize: 13 },
                     }}
                  />
                  <Tooltip title="Copy">
                     <IconButton onClick={copyKey} size="small">
                        <ContentCopyIcon fontSize="small" />
                     </IconButton>
                  </Tooltip>
               </Box>
            </DialogContent>
            <DialogActions>
               <Button variant="contained" onClick={() => setNewKey(null)}>
                  Done
               </Button>
            </DialogActions>
         </Dialog>
      </Box>
   );
}

export default withFirebase(ApiKeys);
